import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AddRounded,
  DeleteOutlineRounded,
  EditRounded,
  PublicRounded
} from '@mui/icons-material';
import { FlowBreadcrumbs, FlowPageHeader, FlowStatGrid, SoftPanel, mediaBaseUrl, truncateText } from './FlowLayout';

const ManageContinent = () => {
  const [continents, setContinents] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get('https://aaryanholidays.in/backend/continents')
      .then((response) => setContinents(response.data))
      .catch((error) => console.error('Error fetching continents:', error));
  }, []);

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this continent?')) {
      axios.delete(`https://aaryanholidays.in/backend/continents/${id}`)
        .then(() => setContinents((current) => current.filter((continent) => continent.id !== id)))
        .catch((error) => console.error('Error deleting continent:', error));
    }
  };

  const filteredContinents = useMemo(() => {
    return continents.filter((item) =>
      String(item.continent || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [continents, search]);

  return (
    <Box>
      <FlowBreadcrumbs items={[{ label: 'Tour Management' }, { label: 'Continents' }]} />

      <FlowPageHeader
        title="Manage Continents"
        subtitle="Organize the first level of the travel hierarchy with quicker search, clear actions, and better visual context."
        search={search}
        searchPlaceholder="Search continents, headings, or descriptions"
        onSearchChange={setSearch}
        action={(
          <Button
            component={Link}
            to="/manage-continents/add"
            variant="contained"
            startIcon={<AddRounded />}
            sx={{ borderRadius: 999, px: 2.5, py: 1.2, textTransform: 'none', fontWeight: 700 }}
          >
            Add Continent
          </Button>
        )}
      />

      <FlowStatGrid
        items={[
          { label: 'Total Continents', value: continents.length, icon: PublicRounded, tone: 'linear-gradient(135deg, #0f766e 0%, #2dd4bf 100%)' }
        ]}
      />

      <SoftPanel>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Continent</TableCell>
                <TableCell>Heading</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Banner</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContinents.map((continent) => (
                <TableRow key={continent.id} hover>
                  <TableCell>{continent.id}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={continent.continent_banner ? `${mediaBaseUrl}${continent.continent_banner}` : undefined}
                        alt={continent.continent}
                        variant="rounded"
                        sx={{ width: 52, height: 52, borderRadius: 3 }}
                      />
                      <Typography sx={{ fontWeight: 700 }}>{continent.continent}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{truncateText(continent.continent_heading, 60)}</TableCell>
                  <TableCell>{truncateText(continent.continent_description, 110)}</TableCell>
                  <TableCell>
                    {continent.continent_banner ? (
                      <img
                        src={`${mediaBaseUrl}${continent.continent_banner}`}
                        alt={continent.continent}
                        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }}
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                      <Tooltip title="Edit continent">
                        <IconButton component={Link} to={`/manage-continents/edit/${continent.id}`}>
                          <EditRounded />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete continent">
                        <IconButton onClick={() => handleDelete(continent.id)}>
                          <DeleteOutlineRounded />
                        </IconButton>
                      </Tooltip>
                      <Button
                        component={Link}
                        to={`/manage-tour-type/${continent.id}`}
                        variant="outlined"
                        sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
                      >
                        Open Countries
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SoftPanel>
    </Box>
  );
};

export default ManageContinent;
